import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from '@tanstack/react-router'
import { useChildAssets } from '../../hooks/useQueries'
import { AddIconButton } from '../ui/add-icon-button'

/**
 * The asset's direct children, as a card at the bottom of its registry tab.
 *
 * It was a section of the right rail, removed by spec 2026-10-25-20: on a
 * building the stacking plan already lists every floor and lot. A floor, a
 * site, a room has no such plan to fall back on, so the list lives on here,
 * below the properties. Hidden when there are none, as before.
 */
export function AssetChildrenSection({ org, assetUid }: { org: string; assetUid: string }) {
    const { t, i18n } = useTranslation('assets')
    const navigate = useNavigate()
    const { data: childAssets = [] } = useChildAssets(org, assetUid, true, true)
    if (childAssets.length === 0) return null

    return (
        <section className="rounded-lg border border-border bg-card p-4" data-testid="asset-children-section">
            <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-foreground">
                    {t('right_panel.children')} <span className="font-normal text-muted-foreground">({childAssets.length})</span>
                </h3>
                <AddIconButton
                    tooltip={t('common:actions.add_child_asset', 'Add a child asset')}
                    onClick={() => navigate({
                        to: '/$org/assets/createBasic',
                        params: { org },
                        search: { parent_asset_uid: assetUid },
                    } as never)}
                    testId="add-child-asset"
                />
            </div>
            <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {childAssets.slice(0, 60).map((child) => {
                    const surface = childSurface(child)
                    return (
                        <li key={child.uid}>
                            <Link
                                to="/$org/assets/$assetUid"
                                params={{ org, assetUid: child.uid }}
                                className="flex items-center gap-2 rounded-md border border-border p-2 text-sm transition-colors hover:bg-muted/50"
                                data-testid={`asset-child-${child.uid}`}
                            >
                                <span className="min-w-0 flex-1 truncate">
                                    <span className="font-medium text-foreground">{child.name || (child as { code?: string }).code || child.uid}</span>
                                    {surface != null && (
                                        <span className="text-muted-foreground" data-testid={`asset-child-surface-${child.uid}`}>
                                            {' · '}{new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }).format(surface)} m²
                                        </span>
                                    )}
                                </span>
                            </Link>
                        </li>
                    )
                })}
            </ul>
        </section>
    )
}

// A child's surface_area (m²) from its ?include=properties payload, whose
// values may arrive base64-encoded JSON. Null when absent or not a number.
function childSurface(child: { properties?: Array<{ code?: string; value?: unknown }> }): number | null {
    const raw = (child.properties ?? []).find((p) => p.code === 'surface_area')?.value
    let v: unknown = raw
    if (typeof raw === 'string') {
        try { v = JSON.parse(atob(raw)) } catch { v = raw }
    }
    const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN
    return Number.isFinite(n) && n > 0 ? n : null
}
